import { ReactNode } from 'react';
import { useFormContext, get, GlobalError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMaskito } from '@maskito/react';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { Input, InputProps } from 'react-field-sizing-content';
import { css } from '@emotion/react';
import { Form } from '@anytag/shared-atoms';
import { DefaultFieldProps } from '@anytag/shared-molecules';
import { cn, formatDynamicErrorMessage, formatNumber } from '@anytag/shared-utils';
import { THEME } from '@anytag/shared-constants';
import { withMaskitoRegister } from './withMaskitoRegister';
import { TransferModalFormValues } from './types';

export interface MoneyFieldProps
  extends DefaultFieldProps<string, string>,
    Omit<InputProps, 'name' | 'value' | 'title' | 'onChange' | 'onSubmit'> {
  numberWithDecimals?: boolean;
  children?: ReactNode;
  dynamicError?: boolean;
  triggerFormRevalidation?: boolean;
  onChangeCallback?: () => void;
  balance?: number;
  hasMaxValue?: boolean;
}

export const MoneyField = ({
  name,
  prefix,
  balance,
  className,
  onKeyDown,
  hasMaxValue,
  dynamicError,
  triggerFormRevalidation
}: MoneyFieldProps) => {
  const {
    watch,
    trigger,
    register,
    formState: { errors }
  } = useFormContext<TransferModalFormValues>();
  const { t } = useTranslation();

  const inputRegister = register('amount');
  const amount = watch('amount');
  const step = watch('step');

  const maskitoRef = useMaskito({
    options: maskitoNumberOptionsGenerator({
      precision: 2,
      thousandSeparator: ',',
      decimalSeparator: '.'
    })
  });

  const error = get(errors, name) as GlobalError; // we can use get for getting nested error messages errors[name.nestedName]
  const errorMessage = dynamicError ? formatDynamicErrorMessage(t, error?.message) : error?.message;

  return (
    <>
      <Form.Field
        className={cn(
          'mx-auto mt-6 flex w-max items-center p-0 text-5xl font-semibold',
          amount ? 'text-black-90' : 'text-black-30',
          className
        )}
      >
        <span css={styles.prefix(amount)}>{prefix}</span>
        <Input
          type="text"
          fieldSizing="content"
          placeholder="10,000"
          onKeyDown={onKeyDown}
          className="focus:outline-none"
          {...withMaskitoRegister(inputRegister, maskitoRef, triggerFormRevalidation ? trigger : undefined)}
        />
      </Form.Field>
      {hasMaxValue ? (
        <div css={styles.errorField(!!(amount && errorMessage))}>
          {step === 'charge'
            ? t(balance ? 'Max to' : 'Max to please recharge', {
                amount: `${prefix}${formatNumber(balance || 0, 0)}`
              })
            : t('Max to', {
                amount: `${prefix}${formatNumber(balance || 0, 0)}`
              })}
        </div>
      ) : (
        <div css={styles.errorField(!!errorMessage)}>{errorMessage}</div>
      )}
    </>
  );
};

const styles = {
  errorField: (hasError: boolean) =>
    css({
      height: '24px',
      fontSize: '14px',
      textAlign: 'center',
      marginTop: '4px',
      width: '100%',
      color: hasError ? THEME.colors.red['50'] : THEME.colors.black['50']
    }),
  prefix: (value: string) =>
    css({
      gridArea: '1 / 1',
      lineHeight: 'normal',
      width: 'max-content',
      color: value ? THEME.text.colors.black.lv1 : THEME.colors.black['30']
    })
};
